/* React hooks */
import {useEffect, useState} from "react";
import useDeceased from "../hooks/useDeceased";
import useData from "../hooks/useData";
import useAuth from "../hooks/useAuth";
import { useParams } from "react-router-dom";
import FormConjointInfos from "./deceased/FormConjointInfos";
import FormFamilyStatusInfos from "./deceased/FormFamilyStatusInfos";
import FormChildInfos from "./deceased/FormChildInfos";
import FormCorrespondentChoice from "./deceased/FormCorrespondentChoice";
import FormAdminChoice from "./deceased/FormAdminChoice";
import ConfirmChildModal from "../components/common/ConfirmChildModal";
import WizardSteps from "../components/common/WizardSteps";
import ViewAdminInfos from "../pages/deceased/visualisation pages/ViewAdminInfos";
import ViewCorrespondentInfos from "../pages/deceased/visualisation pages/ViewCorrespondentInfos";
import DeceasedDetails from "../components/common/DeceasedDetails";
import { capitalize, upperCaseName } from "../helper/tools";
import {
    getAffiliationList,
    getDeceasedAdministrator,
    getDeceasedChildren,
    getDeceasedCorrespondent,
    getDeceasedInfos,
    getDeceasedPartner,
    deleteChildFromDb
} from "../api/pro/deceasedReq";
import {getCountryList} from "../api/admin/addressReq";
import {getEventAddedInfos} from "../api/pro/eventReq";
import FormSignatory from "./deceased/FormSignatory";
import FormThoughts from "./deceased/infos_obs/FormThoughts";
import Icon from "../components/common/Icon";

const WIZARD_STEPS = [
    { number: 1, label: "Situation familiale" },
    { number: 2, label: "Gestionnaire" },
    { number: 3, label: "Validation" },
];

const MARITAL_STATUS_LABELS = {
    1: "Marié(e)",
    2: "Veuf(ve)",
    3: "Divorcé(e)",
    4: "Célibataire",
    5: "Pacsé(e)",
    6: "Union libre",
    7: "Séparé(e)",
};

const REGIME_LABELS = {
    1: "Communauté universelle",
    2: "Séparation des biens",
    3: "Ne sait pas",
    4: "Participation aux acquêts",
};

export default function FormDeceasedComplement({ setShowModal, apiDeceasedInfos: apiDeceasedInfosProp }) {
    const {
        deceasedInfos, setDeceasedInfos,
        adminInfos, setAdminInfos,
        partnerInfos, setPartnerInfos,
        childrenInfos, setChildrenInfos,
        correspondentInfos, setCorrespondentInfos,
        affiliationList, setAffiliationList
    } = useDeceased();
    const {countryList, setCountryList} = useData();
    const [newChild, setNewChild] = useState(0);
    const {id} = useParams();
    const {auth} = useAuth();
    const token = auth.token;
    const [showModalChild, setShowModalChild] = useState(false);
    const [showConfirmChildModal, setShowConfirmChildModal] = useState(false);
    const [childCreatedCount, setChildCreatedCount] = useState(0);
    const [thoughtsTo, setThoughtsTo] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [stepMessage, setStepMessage] = useState({text: "", type: ""});
    const [liveMaritalStatus, setLiveMaritalStatus] = useState();
    const [liveIsConjointAdmin, setLiveIsConjointAdmin] = useState();
    const [liveIsAdminFilled, setLiveIsAdminFilled] = useState(false);
    const [liveIsCorrespondentFilled, setLiveIsCorrespondentFilled] = useState(false);

    const [submitFunctions, setSubmitFunctions] = useState({
        maritalStatus: null,
        conjoint: null,
        regime: null,
        admin: null,
        correspondent: null,
        signatory: null,
        thoughts: null,
    });

    const submitFunctionKeysByStep = {
        1: ["maritalStatus", "conjoint", "regime"],
        2: ["admin", "correspondent", "signatory", "thoughts"],
    };

    const registerSubmitFunction = (formName, submitFn) => {
        setSubmitFunctions(prev => ({
            ...prev,
            [formName]: submitFn
        }));
    };

    const [childSubmitFunctions, setChildSubmitFunctions] = useState({});

    const registerChildSubmitFunction = (childId, submitFn) => {
        setChildSubmitFunctions(prev => ({
            ...prev,
            [childId]: submitFn
        }));
    };

    const apiDeceasedInfos = async () => {
        const response = await getDeceasedInfos(token, id)
        setDeceasedInfos(response)
        if (apiDeceasedInfosProp) {
            apiDeceasedInfosProp();
        }
    };

    const apiAffiliationList = async () => {
        const response = await getAffiliationList(token)
        setAffiliationList(response)
    }

    const apiCountryList = async () => {
        const response = await getCountryList(token)
        setCountryList(response)
    }

    useEffect(() => {

        if (countryList?.length === 0) {
            apiCountryList()
        }
    }, [ token ]);

    const apiAdminInfos = async () => {
        const response = await getDeceasedAdministrator(token, id)
        if (!response?.error) {
            setAdminInfos(response)
        } else {
            setAdminInfos()
        }
    };

    const apiChildrenInfos = async () => {
        const response = await getDeceasedChildren(token, id)
        setChildrenInfos(response)
    };

    // Résout avec un message d'erreur (affiché par l'appelant dans son propre div.errors, comme
    // les autres erreurs de validation) si la suppression échoue, sinon résout sans valeur.
    const deleteChild = (thisChild) => {
        let body = {
            childId : thisChild,
            deceasedId : id
        }
        if (!window.confirm("Voulez-vous vraiment supprimer cet enfant ?")) {
            return Promise.resolve();
        }
        return deleteChildFromDb(token, body)
            .then((response) => {
                if (response?.error) {
                    return response.error === "The child is the deceased admin."
                        ? "Impossible de supprimer cet enfant : il est désigné comme gestionnaire du défunt. Changez d'abord de gestionnaire avant de le supprimer."
                        : response.error;
                }
                apiChildrenInfos()
                apiAdminInfos()
                setShowModalChild(false)
            })
    };

    const apiPartnerInfos = async () => {
        const response = await getDeceasedPartner(token, id)
        setPartnerInfos(response)
    };

    const apiCorrespondentInfos = async () => {
        const response = await getDeceasedCorrespondent(token, id)
        setCorrespondentInfos(response)
    };

    const apiEventAddedInfos = async () => {
        const response = await getEventAddedInfos(token, id)
        setThoughtsTo(response?.thoughtsTo)
    };

    useEffect(() => {
        if (!affiliationList?.length > 0) {
            apiAffiliationList()
        }
    }, [ id ]);

    useEffect(() => {
        // Charger toutes les données quand l'ID change
        apiDeceasedInfos()
        apiPartnerInfos()
        apiAdminInfos()
        apiCorrespondentInfos()
        apiChildrenInfos()
        apiEventAddedInfos()
    }, [ id ]);

    // Scroll to top when modal opens
    useEffect(() => {
        if (showModalChild) {
            const modalElement = document.querySelector('.modal.child-infos');
            if (modalElement) {
                modalElement.scrollTop = 0;
            }
        }
    }, [showModalChild, newChild]);

    const handleChildClick = () => {
        const totalExistingChildren = childrenInfos?.length || 0;
        if (totalExistingChildren < 10) {
            setNewChild(totalExistingChildren + 1);
            setShowModalChild(true)
        }
    };

    const handleConfirmYesChild = () => {
        setShowConfirmChildModal(false);
        setNewChild(prev => prev + 1);
        setShowModalChild(true);
    };

    const handleConfirmNoChild = () => {
        setShowConfirmChildModal(false);
        setChildCreatedCount(0);
    };

    const clearStepMessage = () => setStepMessage({text: "", type: ""});

    const handleStepSubmit = async (step) => {
        const fakeEvent = { preventDefault: () => {} };
        const fns = submitFunctionKeysByStep[step]
            .map(key => submitFunctions[key])
            .filter(Boolean);

        if (step === 1) {
            fns.push(...Object.values(childSubmitFunctions).filter(Boolean));
        }

        try {
            // Séquentiel (pas Promise.all) et dans l'ordre déclaré par submitFunctionKeysByStep :
            // à l'étape 2, le correspondant ("l'adresse est celle du gestionnaire") et le
            // signataire sont résolus côté back à partir du gestionnaire déjà enregistré en base
            // (ex: setDeceasedCorrespondent lit $deceased->getGestionnaire()) — en parallèle, leur
            // requête pouvait s'exécuter avant que celle du gestionnaire soit commitée, et le
            // destinataire ne se complétait alors pas.
            for (const fn of fns) {
                await fn(fakeEvent);
            }

            const invalidFields = document.querySelectorAll(`.wizard-step-panel[data-step="${step}"] .invalid`);
            if (invalidFields.length > 0) {
                setStepMessage({text: "Veuillez remplir tous les champs obligatoires selon le format demandé.", type: "error"});
                return;
            }

            await apiDeceasedInfos();
            await apiPartnerInfos();
            await apiChildrenInfos();
            await apiAdminInfos();
            await apiCorrespondentInfos();
            setCompletedSteps(prev => prev.includes(step) ? prev : [...prev, step]);
            setStepMessage({text: "Informations enregistrées avec succès.", type: "success"});
            setTimeout(clearStepMessage, 3000);
            setCurrentStep(step + 1);
        } catch (error) {
            setStepMessage({text: "Une erreur est survenue lors de l'enregistrement des données.", type: "error"});
        }
    };

    const handlePrevStep = () => {
        clearStepMessage();
        setCurrentStep(prev => Math.max(1, prev - 1));
    };

    const handleStepClick = (step) => {
        clearStepMessage();
        setCurrentStep(step);
    };

    const handleFinalValidate = async () => {
        await apiDeceasedInfos();
        setShowModal(false);
        apiAdminInfos();
        apiPartnerInfos();
        apiCorrespondentInfos();
    };

    const signatoryAddress = deceasedInfos?.gestionnaireSignatory == 1
        ? adminInfos?.address
        : deceasedInfos?.gestionnaireSignatory == 0
            ? correspondentInfos?.address
            : null;

    const signatoryAddressLine = [
        signatoryAddress?.address,
        signatoryAddress?.addressComp,
    ].filter(Boolean).join(', ') + (signatoryAddress?.zipcode || signatoryAddress?.city ?
        ` - ${[signatoryAddress?.zipcode, signatoryAddress?.city].filter(Boolean).join(' ')}` : '');

    return (
        <>
            { showModalChild &&
                <div className="modal child-infos">
                    {(() => {
                        const totalExistingChildren = childrenInfos?.length || 0;

                        if (newChild > 0 && newChild <= totalExistingChildren) {
                            const childToShow = childrenInfos[newChild - 1];
                            return (
                                <FormChildInfos
                                    apiAdminInfos={apiAdminInfos}
                                    apiChildrenInfos={apiChildrenInfos}
                                    apiPartnerInfos={apiPartnerInfos}
                                    deleteChild={deleteChild}
                                    setNewChild={setNewChild}
                                    setShowModal={setShowModalChild}
                                    childId={"" + (newChild - 1)}
                                    child={childToShow}
                                    key={"child" + childToShow?.id}
                                    legend={`Enfant ${newChild}`}
                                    childrenInfos={childrenInfos}
                                    currentChildIndex={newChild - 1}
                                    setShowAdminModal={() => {}}
                                    setShowConfirmChildModal={setShowConfirmChildModal}
                                    setChildCreatedCount={setChildCreatedCount}
                                />
                            );
                        }
                        else if (newChild > totalExistingChildren && newChild <= 10) {
                            const newChildNumber = newChild - totalExistingChildren;
                            return (
                                <FormChildInfos
                                    apiAdminInfos={apiAdminInfos}
                                    apiChildrenInfos={apiChildrenInfos}
                                    apiPartnerInfos={apiPartnerInfos}
                                    deleteChild={deleteChild}
                                    setNewChild={setNewChild}
                                    setShowModal={setShowModalChild}
                                    childId={"new" + newChildNumber}
                                    key={"newChild" + newChildNumber}
                                    legend={`Enfant ${newChild} : `}
                                    setShowAdminModal={() => {}}
                                    setShowConfirmChildModal={setShowConfirmChildModal}
                                    setChildCreatedCount={setChildCreatedCount}
                                />
                            );
                        }
                    })()}
                </div>
            }
            <div className="wizard-form">
                <Icon source="fa fa-info-circle" span="Informations complémentaires du défunt"/>

                <WizardSteps
                    steps={WIZARD_STEPS}
                    currentStep={currentStep}
                    completedSteps={completedSteps}
                    onStepClick={handleStepClick}
                />

                {stepMessage?.text &&
                    <div className="errors step-message">
                        <p className={stepMessage.type === "error" ? "validation-message" : "success-message"}>{stepMessage.text}</p>
                    </div>
                }

                <div className={`wizard-step-panel${currentStep === 1 ? "" : " hidden"}`} data-step={1}>
                    <FormFamilyStatusInfos
                        deceasedInfos={deceasedInfos}
                        apiDeceasedInfos={apiDeceasedInfos}
                        apiPartnerInfos={apiPartnerInfos}
                        setShowModal={() => {}}
                        setShowPartnerModal={() => {}}
                        setShowChildModal={() => {}}
                        setNewChild={setNewChild}
                        setShowConfirmChildModal={setShowConfirmChildModal}
                        setChildCreatedCount={setChildCreatedCount}
                        onMaritalStatusChange={setLiveMaritalStatus}
                        registerSubmitFunction={(fn) => registerSubmitFunction('maritalStatus', fn)}
                        noSubmitButton={true}
                    />

                    {/* Afficher le formulaire du conjoint uniquement si marié(1), pacsé(5), union libre(6) ou séparé(7) */}
                    {["1", "5", "6", "7", 1, 5, 6, 7].includes(liveMaritalStatus) && (
                        <FormConjointInfos
                            setShowModal={() => {}}
                            partnerBirthPlaceId={partnerInfos?.birthplaceId}
                            partnerAddressId={partnerInfos?.addressId}
                            partnerId={partnerInfos?.id}
                            legend="Informations du conjoint"
                            apiAdminInfos={apiAdminInfos}
                            apiDeceasedInfos={apiDeceasedInfos}
                            apiPartnerInfos={apiPartnerInfos}
                            apiCorrespondentInfos={apiCorrespondentInfos}
                            data={partnerInfos}
                            registerSubmitFunction={(fn) => registerSubmitFunction('conjoint', fn)}
                            noSubmitButton={true}
                        />
                    )}

                    {/* Afficher le régime matrimonial uniquement si marié(1), pacsé(5) ou séparé(7) */}
                    {/*{["1", "5", "7", 1, 5, 7].includes(liveMaritalStatus) && (
                        <FormMatrimonialRegime
                            setShowModal={() => {}}
                            apiPartnerInfos={apiPartnerInfos}
                            partnerInfos={partnerInfos}
                            legend="Informations du régime matrimonial"
                            setShowChildModal={() => {}}
                            setNewChild={setNewChild}
                            setShowConfirmChildModal={setShowConfirmChildModal}
                            setChildCreatedCount={setChildCreatedCount}
                            registerSubmitFunction={(fn) => registerSubmitFunction('regime', fn)}
                            noSubmitButton={true}
                        />
                    )}*/}

                    {childrenInfos &&
                        childrenInfos?.map((child, index) => (
                            <FormChildInfos
                                apiAdminInfos={apiAdminInfos}
                                apiChildrenInfos={apiChildrenInfos}
                                apiPartnerInfos={apiPartnerInfos}
                                deleteChild={deleteChild}
                                setNewChild={setNewChild}
                                setShowModal={() => {}}
                                childId={"" + index}
                                child={child}
                                key={"child" + child?.id}
                                legend={`Enfant ${index + 1}`}
                                childrenInfos={childrenInfos}
                                currentChildIndex={index}
                                setShowAdminModal={() => {}}
                                setShowConfirmChildModal={setShowConfirmChildModal}
                                setChildCreatedCount={setChildCreatedCount}
                                registerSubmitFunction={(fn) => registerChildSubmitFunction(child?.id, fn)}
                                noSubmitButton={true}
                            />
                        ))
                    }
                    <button type="button" className="blue-button" onClick={handleChildClick}><i className="fa fa-plus"/><span>Ajouter un enfant</span></button>

                    <div className="buttons-container">
                        <button type="button" className="red-button" onClick={() => setShowModal(false)}>
                            <i className="fa fa-xmark"></i>
                            <span>Annuler</span>
                        </button>
                        <button type="button" className="green-button" onClick={() => handleStepSubmit(1)}>
                            <i className="fa fa-check"></i>
                            <span>Valider</span>
                        </button>
                    </div>
                </div>

                <div className={`wizard-step-panel${currentStep === 2 ? "" : " hidden"}`} data-step={2}>
                    <FormAdminChoice
                        adminInfos={adminInfos}
                        setShowModal={() => {}}
                        deceasedInfos={deceasedInfos}
                        childrenInfos={childrenInfos}
                        apiAdminInfos={apiAdminInfos}
                        apiChildrenInfos={apiChildrenInfos}
                        apiDeceasedInfos={apiDeceasedInfos}
                        data={adminInfos}
                        legend="Identité du gestionnaire"
                        setShowCorrespondentModal={() => {}}
                        onIsConjointAdminChange={setLiveIsConjointAdmin}
                        onAdminFilledChange={setLiveIsAdminFilled}
                        liveMaritalStatus={liveMaritalStatus}
                        registerSubmitFunction={(fn) => registerSubmitFunction('admin', fn)}
                        noSubmitButton={true}
                    />

                    <FormCorrespondentChoice
                        setShowModal={() => {}}
                        deceasedInfos={deceasedInfos}
                        apiDeceasedInfos={apiDeceasedInfos}
                        apiCorrespondentInfos={apiCorrespondentInfos}
                        legend="Identité du correspondant"
                        setShowSignatoryModal={() => {}}
                        liveIsConjointAdmin={liveIsConjointAdmin}
                        liveMaritalStatus={liveMaritalStatus}
                        liveIsAdminFilled={liveIsAdminFilled}
                        onCorrespondentFilledChange={setLiveIsCorrespondentFilled}
                        registerSubmitFunction={(fn) => registerSubmitFunction('correspondent', fn)}
                        noSubmitButton={true}
                    />

                    <FormSignatory
                        apiDeceasedInfos={apiDeceasedInfos}
                        setShowModal={() => {}}
                        liveIsAdminFilled={liveIsAdminFilled}
                        liveIsCorrespondentFilled={liveIsCorrespondentFilled}
                        registerSubmitFunction={(fn) => registerSubmitFunction('signatory', fn)}
                        noSubmitButton={true}
                    />

                    <FormThoughts
                        apiEventAddedInfos={apiEventAddedInfos}
                        apiDeceasedInfos={apiDeceasedInfos}
                        thoughts={thoughtsTo}
                        registerSubmitFunction={(fn) => registerSubmitFunction('thoughts', fn)}
                        noSubmitButton={true}
                    />

                    <div className="buttons-container">
                        <button type="button" className="blue-button" onClick={handlePrevStep}>
                            <i className="fa fa-arrow-left"></i>
                            <span>Étape précédente</span>
                        </button>
                        <button type="button" className="red-button" onClick={() => setShowModal(false)}>
                            <i className="fa fa-xmark"></i>
                            <span>Annuler</span>
                        </button>
                        <button type="button" className="green-button" onClick={() => handleStepSubmit(2)}>
                            <i className="fa fa-check"></i>
                            <span>Valider</span>
                        </button>
                    </div>
                </div>

                <div className={`wizard-step-panel${currentStep === 3 ? "" : " hidden"}`} data-step={3}>
                    <div className="recap-grid">
                        <div className="recap-col">
                            <fieldset>
                                <legend>Situation familiale</legend>
                                <div className="recap-summary recap-summary-spaced">
                                    <p className="recap-relation">{MARITAL_STATUS_LABELS[deceasedInfos?.maritalStatus]}
                                    {["1", "5", "6", "7", 1, 5, 6, 7].includes(deceasedInfos?.maritalStatus) &&
                                        <>: {partnerInfos?.civility == "M" ? "Monsieur" : "Madame"} {capitalize(partnerInfos?.firstName)} {upperCaseName(partnerInfos?.lastName)}</>
                                    }
                                    </p>

                                        {/*{["1", "5", "7", 1, 5, 7].includes(deceasedInfos?.maritalStatus) && partnerInfos?.regime &&
                                            <div className="form-input">
                                                <label>Régime matrimonial</label>
                                                <span>{REGIME_LABELS[partnerInfos?.regime]}</span>
                                            </div>
                                        }*/}
                                        {childrenInfos?.length > 0 &&
                                                <p className="recap-name">Nombre d'enfants : {childrenInfos?.length}</p>
                                        }

                                        {childrenInfos?.map((child, index) => (
                                            <><p className="recap-relation">
                                            {capitalize(child?.firstName)} {upperCaseName(child?.lastName)} {(child?.isMinor) &&
                                                <>- né(é) le {child?.birthDate?.replace(/(\d{4})-(\d{2})-(\d{2})/, '$3/$2/$1')}</>}
                                            {child?.tutor &&
                                                <p className="recap-address">Tuteur: {capitalize(child?.tutor?.firstName)} {upperCaseName(child?.tutor?.lastName)}</p>
                                            }
                                            </p><br/></>
                                        ))}
                                </div>
                            </fieldset>

                            <ViewCorrespondentInfos
                                deceasedInfos={deceasedInfos}
                                apiDeceasedInfos={apiDeceasedInfos}
                                apiCorrespondentInfos={apiCorrespondentInfos}
                                correspondent={correspondentInfos}
                                adminInfos={adminInfos}
                                legend="Destinataire des correspondances"
                            />
                        </div>

                        <div className="recap-col">
                            <ViewAdminInfos
                                apiPartnerInfos={apiPartnerInfos}
                                apiAdminInfos={apiAdminInfos}
                                apiChildrenInfos={apiChildrenInfos}
                                apiDeceasedInfos={apiDeceasedInfos}
                                childrenInfos={childrenInfos}
                                deceasedInfos={deceasedInfos}
                                adminInfos={adminInfos}
                                legend="Gestionnaire"
                                data={adminInfos}
                            />

                            <fieldset>
                                <legend>Signataire des courriers</legend>
                                <div className="recap-summary">
                                    <p className="recap-name">
                                        {deceasedInfos?.gestionnaireSignatory == 1
                                            ? `${adminInfos?.civility == "M" ? "Monsieur " : "Madame "}${capitalize(adminInfos?.firstName)} ${adminInfos?.lastName?.toUpperCase() || ''}`
                                            : deceasedInfos?.gestionnaireSignatory == 0
                                                ? `${correspondentInfos?.civility == "M" ? "Monsieur " : "Madame "}${capitalize(correspondentInfos?.firstName)} ${correspondentInfos?.lastName?.toUpperCase() || ''}`
                                                : "non renseigné"}
                                    </p>
                                    <p className="recap-address"><span>Adresse : </span>{signatoryAddressLine}</p>
                                </div>
                            </fieldset>
                        </div>
                    </div>

                    <div className="buttons-container">
                        <button type="button" className="blue-button" onClick={handlePrevStep}>
                            <i className="fa fa-arrow-left"></i>
                            <span>Étape précédente</span>
                        </button>
                        <button type="button" className="red-button" onClick={() => setShowModal(false)}>
                            <i className="fa fa-xmark"></i>
                            <span>Annuler</span>
                        </button>
                        <button type="button" className="green-button" onClick={handleFinalValidate}>
                            <i className="fa fa-check"></i>
                            <span>Valider</span>
                        </button>
                    </div>
                </div>

                <ConfirmChildModal
                    showModal={showConfirmChildModal}
                    onYes={handleConfirmYesChild}
                    onNo={handleConfirmNoChild}
                    childCount={childCreatedCount}
                />
            </div>
            <DeceasedDetails location="Tableau de bord" admin={adminInfos} deceasedListApi={apiDeceasedInfos} />
        </>
    )
}
